import { useState, useCallback, useEffect, useRef } from 'react';
import { createApiClient } from '../api';
import { saveGuessesToCookie, loadGuessesFromCookie, saveHintsUsed, loadHintsUsed, saveLastHintAtGuessCount, loadLastHintAtGuessCount, cleanupOldGuessCookies, saveWinningWord, loadWinningWord, saveYesterdayMode, loadYesterdayMode } from '../utils/cookies';
import { getYesterdayDayKey, getCurrentDayKey } from '../utils/countdown';

/**
 * Custom hook for Semantle game logic
 * @param {string} apiUrl - Base URL of the API server (empty string for relative URLs in web)
 * @param {string} currentLanguage - Current language code (e.g., 'en', 'el', 'fr')
 * @returns {Object} Game state and handlers
 */
export function useSemantle(apiUrl = '', currentLanguage = 'en') {
  const [word, setWord] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [guesses, setGuesses] = useState([]); // Array of { word, score }
  const [notFoundWords, setNotFoundWords] = useState([]); // Array of words not in vocabulary
  const [vocabularyCheck, setVocabularyCheck] = useState(null); // { exactMatch: boolean, suggestions: string[] }
  const [checkingVocabulary, setCheckingVocabulary] = useState(false);
  // Check localStorage for user preference first, then fall back to server setting
  // Only use localStorage in browser environment, not React Native
  const getInitialClickableSuggestions = () => {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      try {
        const saved = localStorage.getItem('clickableSuggestions');
        if (saved !== null) {
          return saved === 'true';
        }
      } catch (e) {
        // localStorage not available (e.g., React Native)
      }
    }
    return true; // Default to true (clickable suggestions on)
  };
  const [clickableSuggestions, setClickableSuggestions] = useState(getInitialClickableSuggestions()); // Whether suggestions can be clicked
  const [hintLoading, setHintLoading] = useState(false);
  const [hintsUsed, setHintsUsed] = useState(0); // Hints used this day
  const [hintLimit, setHintLimit] = useState(40);
  const [lastHintAtGuessCount, setLastHintAtGuessCount] = useState(-1); // Guess count when last hint was used (-1 = none yet)
  /** Optional map word -> explanation from daily_hints_explainable (empty if missing or fetch failed). */
  const [hintExplanationsByWord, setHintExplanationsByWord] = useState({});
  const [dayKey, setDayKey] = useState(null); // Current day key for cookie caching
  const savedWinningGuessesRef = useRef(new Set()); // Track saved winning guesses to prevent duplicates
  const [winningWord, setWinningWord] = useState(null); // The word that was correctly guessed
  const [isYesterdayMode, setIsYesterdayMode] = useState(false); // Whether we're playing a previous day's word
  const [activeDayKey, setActiveDayKey] = useState(null); // The dayKey we're currently playing (today or a previous day)
  const [currentLang, setCurrentLang] = useState(currentLanguage); // Track current language
  const [reportedValidWord, setReportedValidWord] = useState(null); // Word user reported as valid (show "Reported")
  const [reportValidWordLoading, setReportValidWordLoading] = useState(false);
  const api = createApiClient(apiUrl || (typeof window !== 'undefined' ? '' : 'http://localhost:3000'));

  // Track if we're currently switching languages to prevent saving empty guesses
  const isLanguageSwitchingRef = useRef(false);
  // Track the last language we loaded guesses for (to prevent overwriting with empty)
  const lastLoadedLanguageRef = useRef(null);
  const lastLoadedDayKeyRef = useRef(null);
  // Track current guesses in a ref so we can access them synchronously
  const guessesRef = useRef([]);
  // Track active day so async metadata load can avoid overwriting when user switched to yesterday
  const activeDayKeyRef = useRef(null);
  // Track if guesses were just loaded (to prevent overwriting with empty after load)
  const guessesJustLoadedRef = useRef(false);
  const hintsUsedRef = useRef(0);
  
  // Keep guessesRef in sync with guesses state
  useEffect(() => {
    guessesRef.current = guesses;
  }, [guesses]);
  
  useEffect(() => {
    hintsUsedRef.current = hintsUsed;
  }, [hintsUsed]);
  
  // Keep activeDayKeyRef in sync so metadata effect's async load doesn't overwrite yesterday view
  useEffect(() => {
    activeDayKeyRef.current = activeDayKey;
  }, [activeDayKey]);

  // Load optional explainable-hint copy for the active puzzle day (no-op if server has no file).
  useEffect(() => {
    if (!activeDayKey) return;
    const lang = currentLang || 'en';
    let cancelled = false;
    const client = createApiClient(apiUrl || (typeof window !== 'undefined' ? '' : 'http://localhost:3000'));
    (async () => {
      try {
        const data = await client.getHintExplanations(lang, activeDayKey);
        if (cancelled) return;
        if (activeDayKeyRef.current !== activeDayKey) return;
        const exp = data && data.explanations && typeof data.explanations === 'object' && !Array.isArray(data.explanations)
          ? data.explanations
          : {};
        setHintExplanationsByWord(exp);
      } catch {
        if (!cancelled && activeDayKeyRef.current === activeDayKey) {
          setHintExplanationsByWord({});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeDayKey, currentLang, apiUrl]);

  // Attach explainable copy from the day map onto each guess so ★/width match the flying chip
  // and do not pop in later (cookie restore, or map finishing after guesses render).
  useEffect(() => {
    const map = hintExplanationsByWord;
    if (!map || typeof map !== 'object' || Array.isArray(map)) return;
    if (Object.keys(map).length === 0) return;
    setGuesses((prev) => {
      let changed = false;
      const next = prev.map((g) => {
        if (!g || typeof g.word !== 'string') return g;
        if (typeof g.hintExplanation === 'string' && g.hintExplanation.trim()) return g;
        const raw = map[g.word.toLowerCase()];
        if (typeof raw === 'string' && raw.trim()) {
          changed = true;
          return { ...g, hintExplanation: raw.trim() };
        }
        return g;
      });
      return changed ? next : prev;
    });
  }, [hintExplanationsByWord, guesses]);

  // Clear suggestion box when user starts typing (so it disappears as they type)
  useEffect(() => {
    setVocabularyCheck(prev => {
      if (prev && prev.suggestions && prev.suggestions.length > 0) return null;
      return prev;
    });
  }, [word]);

  // Update current language when it changes - clear guesses immediately, metadata effect will load new language's guesses
  useEffect(() => {
    if (currentLang !== currentLanguage) {
      isLanguageSwitchingRef.current = true; // Mark that we're switching languages
      setCurrentLang(currentLanguage);
      // Clear guesses immediately so old language guesses don't show
      // The metadata effect will then load the correct guesses for the new language
      setGuesses([]);
      setNotFoundWords([]);
      setWinningWord(null);
      setResult(null);
      setWord('');
      setVocabularyCheck(null);
      setReportedValidWord(null);
      setError(null);
      // Reset the flag after metadata effect has loaded new guesses
      setTimeout(() => {
        isLanguageSwitchingRef.current = false;
      }, 1000);
    }
  }, [currentLanguage, currentLang]);

  // Helper to initialize state for a given day key and language
  const initializeDayState = useCallback(async (newDayKey, lang, options = {}) => {
    const { cancelledRef } = options;

    const prevDayKey = dayKey;

    // If dayKey changed, clear current guesses, hints, and winning state first
    if (prevDayKey && prevDayKey !== newDayKey) {
      setGuesses([]);
      setNotFoundWords([]);
      setHintsUsed(0);
      setLastHintAtGuessCount(-1);
      setWinningWord(null);
      setResult(null);
      setIsYesterdayMode(false);
      // Clear yesterday mode preference when day changes (new day = start fresh)
      try {
        await saveYesterdayMode(false);
      } catch (_) {}
    }

    setDayKey(newDayKey);
    // Always default to today's word on initial load
    // Don't restore yesterday mode automatically - user must explicitly choose it
    setActiveDayKey(newDayKey);
    activeDayKeyRef.current = newDayKey;

    // Always load guesses for the current language and day (storage is separate per language and day)
    // This ensures switching languages loads the correct guesses
    // Storage key format: semantle-guesses-${lang}-${dayKey}
    // Only apply loaded guesses if user hasn't switched to yesterday in the meantime (each day has its own guesses)
    try {
      const cachedGuesses = await loadGuessesFromCookie(newDayKey, lang);
      if (cancelledRef && cancelledRef.current) return;
      if (activeDayKeyRef.current === newDayKey) {
        if (cachedGuesses && cachedGuesses.length > 0) {
          setGuesses(cachedGuesses);
          lastLoadedLanguageRef.current = lang;
          lastLoadedDayKeyRef.current = newDayKey;
          guessesJustLoadedRef.current = false; // We loaded guesses, so it's fine to save
        } else {
          // No guesses found for this language/day - only clear if we don't already have in-memory guesses for same day/lang
          // This prevents opening settings (or effect re-run) from wiping guesses when storage returns empty
          const alreadyHaveGuessesForThisDay =
            lastLoadedLanguageRef.current === lang &&
            lastLoadedDayKeyRef.current === newDayKey &&
            guessesRef.current.length > 0;
          if (!alreadyHaveGuessesForThisDay) {
            setGuesses([]);
            setNotFoundWords([]);
          }
          lastLoadedLanguageRef.current = lang;
          lastLoadedDayKeyRef.current = newDayKey;
          guessesJustLoadedRef.current = true;
          setTimeout(() => {
            guessesJustLoadedRef.current = false;
          }, 2000);
        }
      }
    } catch (err) {
      if (cancelledRef && cancelledRef.current) return;
      if (activeDayKeyRef.current === newDayKey) {
        setGuesses([]);
        setNotFoundWords([]);
      }
    }

    // Load hints used and last-hint guess count for this day/language
    try {
      const cachedHintsUsed = await loadHintsUsed(newDayKey, lang);
      const cachedLastHintAt = await loadLastHintAtGuessCount(newDayKey, lang);
      if (cancelledRef && cancelledRef.current) return;
      if (activeDayKeyRef.current === newDayKey) {
        setHintsUsed(Number(cachedHintsUsed) || 0);
        setLastHintAtGuessCount(typeof cachedLastHintAt === 'number' ? cachedLastHintAt : -1);
      }
    } catch (err) {
      if (cancelledRef && cancelledRef.current) return;
      if (activeDayKeyRef.current === newDayKey) {
        setHintsUsed(0);
        setLastHintAtGuessCount(-1);
      }
    }

    // Always load winning word for the current language and day (only apply if still viewing this day)
    try {
      const cachedWinningWord = await loadWinningWord(newDayKey, lang);
      if (cancelledRef && cancelledRef.current) return;
      if (activeDayKeyRef.current === newDayKey) {
        if (cachedWinningWord) {
          setWinningWord(cachedWinningWord);
          // Set result to show congratulations screen
          setResult({ congratulations: true });
        } else {
          // No winning word found for this language/day
          setWinningWord(null);
          setResult(prev => (prev?.congratulations ? prev : null));
        }
      }
    } catch (err) {
      if (cancelledRef && cancelledRef.current) return;
      if (activeDayKeyRef.current === newDayKey) {
        setWinningWord(null);
        setResult(prev => (prev?.congratulations ? prev : null));
      }
    }

    // Clean up old storage
    try {
      await cleanupOldGuessCookies(newDayKey);
    } catch (err) {}
  }, [dayKey]);

  // Load metadata (clickableSuggestions and dayKey) from API and restore guesses from storage
  // Reload when language changes
  useEffect(() => {
    const cancelledRef = { current: false };
    // Ensure we have a valid language (default to 'en' if not set)
    const lang = currentLang || 'en';
    const clientDayKey = getCurrentDayKey();
    api.getMeta(lang, clientDayKey)
      .then(async meta => {
        if (cancelledRef.current) return;
        // Use saved user preference if set; otherwise default to true (clickable suggestions on)
        if (meta.clickableSuggestions !== undefined) {
          if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
            try {
              const userPreference = localStorage.getItem('clickableSuggestions');
              if (userPreference !== null) {
                const userValue = userPreference === 'true';
                setClickableSuggestions(userValue);
              } else {
                setClickableSuggestions(true);
              }
            } catch (e) {
              // localStorage not available (e.g., React Native), try AsyncStorage then default true
              try {
                const AsyncStorage = typeof global !== 'undefined' && global.AsyncStorage;
                if (AsyncStorage) {
                  const userPreference = await AsyncStorage.getItem('clickableSuggestions');
                  if (userPreference !== null) {
                    const userValue = userPreference === 'true';
                    setClickableSuggestions(userValue);
                  } else {
                    setClickableSuggestions(true);
                  }
                } else {
                  setClickableSuggestions(true);
                }
              } catch (e2) {
                setClickableSuggestions(true);
              }
            }
          } else {
            setClickableSuggestions(true);
          }
        }

        if (typeof meta.hintLimitPerIp === 'number' && Number.isFinite(meta.hintLimitPerIp)) {
          setHintLimit(Math.max(1, Math.floor(meta.hintLimitPerIp)));
        } else {
          setHintLimit(40);
        }

        // Use client's local dayKey (user-facing timezone) - server returns it after loading that day's data
        const dayKeyToUse = meta.dayKey || clientDayKey;
        if (dayKeyToUse) {
          await initializeDayState(dayKeyToUse, lang, { cancelledRef });
        }
      })
      .catch(async () => {
        if (cancelledRef.current) return;
        setClickableSuggestions(false);
        setHintLimit(40);
        setError('Cannot reach server. Check internet and try again.');
        const fallbackDayKey = getCurrentDayKey();
        setDayKey(fallbackDayKey);
        setActiveDayKey(fallbackDayKey);
        activeDayKeyRef.current = fallbackDayKey;
        // Still load guesses from storage when offline so they persist across app restarts
        const lang = currentLang || 'en';
        try {
          const cachedGuesses = await loadGuessesFromCookie(fallbackDayKey, lang);
          if (!cancelledRef.current && cachedGuesses && cachedGuesses.length > 0) {
            setGuesses(cachedGuesses);
            lastLoadedLanguageRef.current = lang;
            lastLoadedDayKeyRef.current = fallbackDayKey;
          }
          const cachedWinningWord = await loadWinningWord(fallbackDayKey, lang);
          if (!cancelledRef.current && cachedWinningWord) {
            setWinningWord(cachedWinningWord);
            setResult({ congratulations: true });
          }
        } catch (loadErr) {}
      });

    return () => {
      cancelledRef.current = true;
    };
  }, [apiUrl, currentLang, initializeDayState]); // Reload when language changes
  
  // Save guesses to cookie whenever they change (use activeDayKey for yesterday mode, include language)
  // NEVER save empty guesses - only save when guesses.length > 0 (user made actual guesses)
  // Await so persistence completes before effect finishes (helps when app is closed immediately after a guess)
  useEffect(() => {
    if (!activeDayKey) return;
    if (guesses.length === 0) return;
    const lang = currentLang || 'en';
    let mounted = true;
    saveGuessesToCookie(activeDayKey, guesses, lang).then(() => {
      if (!mounted) return;
      // Save completed
    });
    return () => { mounted = false; };
  }, [activeDayKey, guesses, currentLang]);

  // Expose for app to call when going to background (e.g. AppState) so guesses are persisted before process may suspend
  const persistGuessesNow = useCallback(async () => {
    if (!activeDayKey || guesses.length === 0) return;
    const lang = currentLang || 'en';
    await saveGuessesToCookie(activeDayKey, guesses, lang);
  }, [activeDayKey, guesses, currentLang]);

  // Exposed helper for mobile/web apps: when the app returns to foreground (or on demand),
  // check if the server "day" has advanced and, if so, reset state to the new day's puzzle.
  const refreshForNewDayIfNeeded = useCallback(async () => {
    // If we don't have a day yet, nothing to refresh
    if (!dayKey) return;

    // If user is explicitly playing a previous day, don't auto-switch them
    if (isYesterdayMode && activeDayKey && activeDayKey !== dayKey) {
      return;
    }

    const lang = currentLang || 'en';
    const currentKey = getCurrentDayKey();

    // If our cached day already matches the server timezone's current key, no-op
    if (currentKey === dayKey) return;

    // Initialize state for the new day key using the same logic as initial load.
    // This will clear guesses, reset yesterday mode, and load any stored progress for the new day.
    await initializeDayState(currentKey, lang);
  }, [dayKey, currentLang, isYesterdayMode, activeDayKey, initializeDayState]);


  const handleSubmit = useCallback(async (language = 'en', wordToSubmit = null, options = {}) => {
    const wordToCheck = wordToSubmit || word;
    if (!wordToCheck.trim()) return;

    const skipVocabularyCheck = options.skipVocabularyCheck === true;

    // Check against vocabulary first (unless applying a suggestion - suggestions are already valid)
    if (!skipVocabularyCheck) {
      setCheckingVocabulary(true);
      setVocabularyCheck(null);
      setError(null);
      setResult(null);

      let closestWords = null;
      try {
        // Add timeout to prevent hanging on large vocabularies (10 seconds for English, 5 for others)
        const timeoutMs = language === 'en' ? 10000 : 5000;
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Vocabulary check timeout')), timeoutMs);
        });

        closestWords = await Promise.race([
          api.findClosestWords(wordToCheck.trim(), language, 5, activeDayKey),
          timeoutPromise
        ]);

        // Check if exactMatch is explicitly false (not just falsy)
        // This handles cases where exactMatch might be undefined
        if (closestWords && closestWords.exactMatch === false) {
          setVocabularyCheck({
            exactMatch: false,
            word: wordToCheck.trim(),
            suggestions: closestWords.suggestions || [],
          });
          setError(null); // Don't show error message, just show suggestions
          setCheckingVocabulary(false);
          return;
        }

        // If exactMatch is true or undefined (fallback), allow submission
        // Exact match found - clear any previous vocabulary check
        setVocabularyCheck(null);
      } catch (err) {
        // If service unavailable or timeout, allow submission (fallback behavior)
        setVocabularyCheck(null);
      } finally {
        setCheckingVocabulary(false);
      }
    } else {
      setVocabularyCheck(null);
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const data = await api.submitGuess(wordToCheck.trim(), language, activeDayKey);

      setResult(data);

      const normalizedWord = wordToCheck.trim().toLowerCase();

      // If congratulations, store the winning word
      if (data.congratulations) {
        setWinningWord(normalizedWord);
        // Save winning word to storage for persistence across refreshes (use activeDayKey for yesterday mode, include language)
        if (activeDayKey) {
          const lang = currentLang || 'en';
          saveWinningWord(activeDayKey, normalizedWord, lang).catch(() => {});
          
          // Save winning guess to database (include all previous guesses + winning word)
          // Use a unique key to prevent duplicate saves
          const saveKey = `${activeDayKey}:${lang}:${normalizedWord}`;
          
          // Only save if we haven't already saved this winning guess
          if (!savedWinningGuessesRef.current.has(saveKey)) {
            savedWinningGuessesRef.current.add(saveKey);
            
            // Get current guesses from ref (updated synchronously)
            const currentGuesses = guessesRef.current;
            const winningGuess = { word: normalizedWord, score: 100, percentile: 100 };
            const allGuesses = [...currentGuesses, winningGuess].sort((a, b) => b.score - a.score);
            
            // Update state
            setGuesses(allGuesses);
            
            // Save to database OUTSIDE of setState to prevent duplicate saves
            api.saveWinningGuess(activeDayKey, lang, normalizedWord, allGuesses).catch(() => {
              // Remove from saved set on error so it can be retried
              savedWinningGuessesRef.current.delete(saveKey);
            });
          }
        }
      }

      // If the word has a similarity score, add it to the guesses list
      if (data.inVocab && typeof data.score === 'number') {
        // Remove from notFoundWords if it was there
        setNotFoundWords(prev => prev.filter(w => w !== normalizedWord));

        const fromApi =
          typeof data.hintExplanation === 'string' && data.hintExplanation.trim()
            ? data.hintExplanation.trim()
            : null;

        setGuesses(prevGuesses => {
          // Check if word already exists, update it if it does
          const existingIndex = prevGuesses.findIndex(g => g.word === normalizedWord);
          const percentile = data.percentile !== undefined ? data.percentile : null;
          const prevExpl =
            existingIndex >= 0 && typeof prevGuesses[existingIndex]?.hintExplanation === 'string'
              ? prevGuesses[existingIndex].hintExplanation.trim()
              : null;
          const hintExplanation = fromApi || prevExpl || null;
          const guessRow = {
            word: normalizedWord,
            score: data.score,
            percentile,
            ...(hintExplanation ? { hintExplanation } : {}),
          };
          let updatedGuesses;
          if (existingIndex >= 0) {
            const updated = [...prevGuesses];
            updated[existingIndex] = guessRow;
            updatedGuesses = updated.sort((a, b) => b.score - a.score);
          } else {
            // Add new guess and sort by score descending
            updatedGuesses = [...prevGuesses, guessRow]
              .sort((a, b) => b.score - a.score);
          }
          
          return updatedGuesses;
        });
        setWord(''); // Clear text box after valid word so latest-word highlight stays visible
      } else if (!data.congratulations && !data.inVocab) {
        // Word not found - add to notFoundWords list (if not already there)
        setNotFoundWords(prev => {
          if (!prev.includes(normalizedWord)) {
            return [...prev, normalizedWord].sort();
          }
          return prev;
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [word, api, activeDayKey, currentLang]);

  const checkVocabulary = useCallback(async (text, language = null) => {
    if (!text || !text.trim()) {
      setVocabularyCheck(null);
      return;
    }

    // Use provided language or fall back to current language (default to 'en' if not set)
    const lang = language || currentLang || 'en';

    setCheckingVocabulary(true);
    try {
      const result = await api.findClosestWords(text.trim(), lang, 5, activeDayKey);
      setVocabularyCheck(result);
    } catch (err) {
      // Silently fail - don't show error for vocabulary check
      setVocabularyCheck(null);
    } finally {
      setCheckingVocabulary(false);
    }
  }, [api, activeDayKey, currentLang]);

  const HINT_LIMIT = Math.max(1, Math.floor(Number(hintLimit) || 40));
  const GUESSES_REQUIRED_BEFORE_HINT = 10;
  const lastHintAtGuessCountRef = useRef(-1);
  useEffect(() => {
    lastHintAtGuessCountRef.current = lastHintAtGuessCount;
  }, [lastHintAtGuessCount]);

  const requestHint = useCallback(async (onHintWord = null) => {
    setError(null);
    if (hintsUsedRef.current >= HINT_LIMIT) {
      setError('HINTS_LIMIT_REACHED');
      return;
    }
    const guessCount = guessesRef.current.length;
    const lastAt = lastHintAtGuessCountRef.current;
    if (lastAt < 0 && guessCount < GUESSES_REQUIRED_BEFORE_HINT) {
      const needed = GUESSES_REQUIRED_BEFORE_HINT - guessCount;
      setError(`HINT_NEED_GUESSES:${needed}`);
      return;
    }
    if (lastAt >= 0 && guessCount < lastAt + GUESSES_REQUIRED_BEFORE_HINT) {
      const needed = lastAt + GUESSES_REQUIRED_BEFORE_HINT - guessCount;
      setError(`HINT_NEED_MORE_GUESSES:${needed}`);
      return;
    }
    setHintLoading(true);
    try {
      const guessedWords = guessesRef.current.map((g) => g.word);
      const bestScore = guessedWords.length > 0
        ? Math.max(...guessesRef.current.map((g) => g.score))
        : null;
      const data = await api.getHint(currentLang || 'en', activeDayKey, guessedWords, bestScore);
      const hintWord = data.word;
      if (typeof onHintWord === 'function') {
        onHintWord(hintWord);
      }
      await handleSubmit(currentLang || 'en', hintWord, { skipVocabularyCheck: true });
      const lang = currentLang || 'en';
      setHintsUsed((prev) => {
        const next = Math.min(HINT_LIMIT, prev + 1);
        saveHintsUsed(activeDayKey, next, lang).catch(() => {});
        return next;
      });
      const newCount = guessCount + 1;
      setLastHintAtGuessCount(newCount);
      saveLastHintAtGuessCount(activeDayKey, newCount, lang).catch(() => {});
    } catch (err) {
      setError(err.message);
      if (err.code === 'HINTS_LIMIT_REACHED') {
        if (typeof err.hintLimit === 'number' && Number.isFinite(err.hintLimit)) {
          setHintLimit(Math.max(1, Math.floor(err.hintLimit)));
        }
        setHintsUsed(HINT_LIMIT);
        saveHintsUsed(activeDayKey, HINT_LIMIT, currentLang || 'en').catch(() => {});
      }
    } finally {
      setHintLoading(false);
    }
  }, [api, activeDayKey, currentLang, handleSubmit, hintLimit]);

  const reset = useCallback(() => {
    setWord('');
    setResult(null);
    setError(null);
    setGuesses([]);
    setNotFoundWords([]);
    setVocabularyCheck(null);
    setReportedValidWord(null);
    setWinningWord(null);
    setHintsUsed(0);
    setLastHintAtGuessCount(-1);
    // Clear cookie cache for active day and language
    if (activeDayKey) {
      const lang = currentLang || 'en';
      saveGuessesToCookie(activeDayKey, [], lang);
      saveHintsUsed(activeDayKey, 0, lang).catch(() => {});
      saveLastHintAtGuessCount(activeDayKey, -1, lang).catch(() => {});
    }
  }, [activeDayKey, currentLang]);

  // Function to switch to a previous day
  const playPreviousDay = useCallback(async (targetDayKey) => {
    if (!dayKey || !targetDayKey) return;
    
    // Clear guesses *before* changing activeDayKey so the save effect never runs with
    // (activeDayKey=yesterday, guesses=today's), which would overwrite yesterday's storage
    setGuesses([]);
    setNotFoundWords([]);
    setWinningWord(null);
    setResult(null);
    setError(null);
    setWord('');
    setIsYesterdayMode(true);
    setActiveDayKey(targetDayKey);
    activeDayKeyRef.current = targetDayKey;
    
    // Save yesterday mode preference with the target day key
    await saveYesterdayMode(true);
    
    // Load previous day's guesses and hints for current language
    const lang = currentLang || 'en';
    try {
      const cachedGuesses = await loadGuessesFromCookie(targetDayKey, lang);
      if (cachedGuesses && cachedGuesses.length > 0) {
        setGuesses(cachedGuesses);
      } else {
        setGuesses([]);
      }
    } catch (err) {
      setGuesses([]);
    }
    try {
      const cachedHintsUsed = await loadHintsUsed(targetDayKey, lang);
      setHintsUsed(Number(cachedHintsUsed) || 0);
    } catch (err) {
      setHintsUsed(0);
    }
    try {
      const cachedLastHintAt = await loadLastHintAtGuessCount(targetDayKey, lang);
      setLastHintAtGuessCount(typeof cachedLastHintAt === 'number' ? cachedLastHintAt : -1);
    } catch (err) {
      setLastHintAtGuessCount(-1);
    }
    
    // Load previous day's winning word for current language
    try {
      const cachedWinningWord = await loadWinningWord(targetDayKey, lang);
      if (cachedWinningWord) {
        setWinningWord(cachedWinningWord);
        setResult({ congratulations: true });
      } else {
        setWinningWord(null);
      }
    } catch (err) {
      setWinningWord(null);
    }
  }, [dayKey, currentLang]);
  
  // Keep playYesterday for backward compatibility (now calls playPreviousDay)
  const playYesterday = useCallback(async () => {
    if (!dayKey) return;
    const yesterdayKey = getYesterdayDayKey();
    await playPreviousDay(yesterdayKey);
  }, [dayKey, playPreviousDay]);

  // Function to switch back to today mode
  const playToday = useCallback(async () => {
    if (!dayKey) return;
    
    // Clear guesses before changing activeDayKey so we never save yesterday's guesses to today's key
    setGuesses([]);
    setNotFoundWords([]);
    setWinningWord(null);
    setResult(null);
    setError(null);
    setWord('');
    setIsYesterdayMode(false);
    setActiveDayKey(dayKey);
    activeDayKeyRef.current = dayKey;
    
    // Save yesterday mode preference (false = playing today)
    await saveYesterdayMode(false);
    
    // Load today's guesses and hints for current language
    const lang = currentLang || 'en';
    try {
      const cachedGuesses = await loadGuessesFromCookie(dayKey, lang);
      if (cachedGuesses && cachedGuesses.length > 0) {
        setGuesses(cachedGuesses);
      } else {
        setGuesses([]);
      }
    } catch (err) {
      setGuesses([]);
    }
    try {
      const cachedHintsUsed = await loadHintsUsed(dayKey, lang);
      setHintsUsed(Number(cachedHintsUsed) || 0);
    } catch (err) {
      setHintsUsed(0);
    }
    try {
      const cachedLastHintAt = await loadLastHintAtGuessCount(dayKey, lang);
      setLastHintAtGuessCount(typeof cachedLastHintAt === 'number' ? cachedLastHintAt : -1);
    } catch (err) {
      setLastHintAtGuessCount(-1);
    }
    
    // Load today's winning word for current language
    try {
      const cachedWinningWord = await loadWinningWord(dayKey, lang);
      if (cachedWinningWord) {
        setWinningWord(cachedWinningWord);
        setResult({ congratulations: true });
      } else {
        setWinningWord(null);
      }
    } catch (err) {
      setWinningWord(null);
    }
  }, [dayKey, currentLang]);

  // Function to use a suggestion (click on it)
  const applySuggestion = useCallback(async (suggestedWord, language = null) => {
    const wordToSubmit = (suggestedWord && typeof suggestedWord === 'string') ? suggestedWord.trim() : '';
    if (!wordToSubmit) return;
    const wordLower = wordToSubmit.toLowerCase();
    const alreadyGuessed = guessesRef.current.some(g => g.word.toLowerCase() === wordLower);
    if (alreadyGuessed) {
      setVocabularyCheck(null);
      return;
    }
    // Clear suggestions UI and show loading immediately so tap is visible
    setVocabularyCheck(null);
    setWord(wordToSubmit);
    setError(null);
    setResult(null);
    const lang = language || currentLang || 'en';
    // Skip vocabulary check - suggestion came from API so it's valid
    await handleSubmit(lang, wordToSubmit, { skipVocabularyCheck: true });
  }, [handleSubmit, currentLang]);

  // Report word as valid (user says dictionary is wrong)
  const reportValidWord = useCallback(async () => {
    if (!vocabularyCheck?.word) return;
    setReportValidWordLoading(true);
    try {
      const result = await api.reportValidWord(vocabularyCheck.word, currentLang, activeDayKey);
      if (result.ok) {
        setReportedValidWord(vocabularyCheck.word);
        // After "Reported" appears, close suggestions panel, clear input, and go back to guessing
        setTimeout(() => {
          setVocabularyCheck(null);
          setWord('');
        }, 1200);
      }
    } finally {
      setReportValidWordLoading(false);
    }
  }, [vocabularyCheck?.word, currentLang, activeDayKey, api]);

  // Function to update clickable suggestions preference
  const setClickableSuggestionsPreference = useCallback((enabled) => {
    setClickableSuggestions(enabled);
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      try {
        if (localStorage.getItem('logiword_cookie_consent') === 'functional') {
          localStorage.setItem('clickableSuggestions', enabled ? 'true' : 'false');
        }
      } catch (e) {
        // localStorage not available (e.g., React Native), try AsyncStorage
        const AsyncStorage = typeof global !== 'undefined' && global.AsyncStorage;
        if (AsyncStorage) {
          AsyncStorage.setItem('clickableSuggestions', enabled ? 'true' : 'false').catch(() => {});
        }
      }
    } else {
      // React Native: use global.AsyncStorage if set by the app
      const AsyncStorage = typeof global !== 'undefined' && global.AsyncStorage;
      if (AsyncStorage) {
        AsyncStorage.setItem('clickableSuggestions', enabled ? 'true' : 'false').catch(() => {});
      }
    }
  }, []);

  return {
    // State
    apiUrl: apiUrl || '',
    word,
    loading,
    result,
    error,
    guesses,
    notFoundWords,
    vocabularyCheck,
    checkingVocabulary,
    clickableSuggestions,
    reportedValidWord,
    reportValidWordLoading,
    hintLoading,
    hintsUsed,
    hintLimit,
    lastHintAtGuessCount,
    hintExplanationsByWord,
    winningWord,
    isYesterdayMode,
    activeDayKey,
    // Setters
    setWord,
    setClickableSuggestions: setClickableSuggestionsPreference,
    setVocabularyCheck,
    // Handlers
    handleSubmit,
    applySuggestion,
    reportValidWord,
    requestHint,
    checkVocabulary,
    reset,
    playYesterday,
    playPreviousDay,
    playToday,
    persistGuessesNow,
    refreshForNewDayIfNeeded,
  };
}
